import { useCallback, MouseEvent } from 'react';
import { DropEvent, FileRejection, DropzoneOptions } from 'react-dropzone';

import { UploadProps, LoadedFile, OnFileDelete } from '@hh.ru/magritte-ui-upload/types';

interface UseDropActionsProps {
    onDropAccepted: UploadProps['onDropAccepted'];
    onDropRejected: UploadProps['onDropRejected'];
    onFileDelete: UploadProps['onFileDelete'];
}

interface UseDropActionsResult {
    onDropAccepted: DropzoneOptions['onDropAccepted'];
    onDropRejected: DropzoneOptions['onDropRejected'];
    onFileDelete: OnFileDelete;
}

const useDropActions = ({
    onDropAccepted: onDropAcceptedExternal,
    onDropRejected: onDropRejectedExternal,
    onFileDelete: onFileDeleteExternal,
}: UseDropActionsProps): UseDropActionsResult => {
    const onDropAccepted = useCallback(
        (acceptedFiles: File[], event: DropEvent) => {
            const acceptedLoadFiles = acceptedFiles.map((file) => ({ data: file, loading: true, loadPercentage: 0 }));
            onDropAcceptedExternal(acceptedLoadFiles, event);
        },
        [onDropAcceptedExternal]
    );
    const onDropRejected = useCallback(
        (fileRejections: FileRejection[], event: DropEvent) => {
            const rejectionLoadFiles = fileRejections.map((fileRejection) => ({
                data: fileRejection.file,
                loading: false,
                error: fileRejection.errors?.[0].code || '',
            }));
            onDropRejectedExternal?.(rejectionLoadFiles, event);
        },
        [onDropRejectedExternal]
    );
    const onFileDelete = useCallback(
        (event: MouseEvent, loadedFile: LoadedFile | undefined, indexFile: number) => {
            loadedFile && onFileDeleteExternal(loadedFile, indexFile);
            event.stopPropagation();
        },
        [onFileDeleteExternal]
    );
    return { onDropAccepted, onDropRejected, onFileDelete };
};

export { useDropActions };
